/**
 * The feedback-pin ANCHOR SEAM (Issue #13578, epic #13573).
 *
 * Three sub-issues meet here and none of them may import each other's
 * internals:
 *
 *  - **F1 (#13574)** owns the storage (`selectorPath`, `pinRect`, `route` on
 *    `Feedback`) and the org-scoped, visibility-filtered fetch that turns it
 *    into rows. It PRODUCES {@link FeedbackPinAnchor}.
 *  - **F2 (#13575, landed)** owns `src/lib/feedback/selector-path.ts`, which
 *    turns a `selectorPath` back into an `Element`.
 *  - **F3 (this issue)** owns the overlay that renders a pin per anchor. It
 *    CONSUMES {@link FeedbackPinAnchor} and calls F2's resolver.
 *
 * F1 is not merged, so this module declares the shape rather than importing
 * it. `src/components/feedback/pin-overlay.tsx` takes its anchors as a prop
 * typed by {@link FeedbackPinAnchor} and, optionally, a
 * {@link FeedbackPinAnchorLoader} — so when F1 lands, wiring it up is passing
 * its fetch function in, not rewriting the overlay. An overlay that imported
 * a `listRouteAnchors` that does not exist yet would not typecheck at all.
 *
 * Everything here is PURE and DOM-free on purpose: it is imported by a client
 * component, so it must not reach for `server-only`, Prisma or `document`.
 * `FeedbackStatus` is a type-only import and erases at compile time.
 */

import type { FeedbackStatus } from '@prisma/client-cadence'

/**
 * A capture-time bounding box, in CSS pixels relative to the document.
 *
 * Stored by F1 at capture time and used ONLY as the fallback when
 * `selectorPath` no longer resolves — a resolved element is always measured
 * live, because a stored rect goes stale the moment the page reflows.
 */
export interface PinRect {
  top: number
  left: number
  width: number
  height: number
}

/** Viewport dimensions, used to rescale a stale {@link PinRect}. */
export interface PinViewport {
  width: number
  height: number
}

/**
 * One feedback thread anchored to one element on one route.
 *
 * The field set is deliberately the minimum the overlay needs to RENDER:
 * anything the popover needs beyond this (the comment thread, the submitter,
 * the timestamps) is fetched per-thread on open through the existing
 * `getFeedbackDetail` action, which already enforces the same
 * admin-or-author visibility rule F1 applies to the list. Duplicating the
 * thread into every anchor would put every comment on every page load.
 */
export interface FeedbackPinAnchor {
  /** `Feedback.id` — the thread this pin opens. */
  feedbackId: string
  /** The route the pin was captured on. Compared with {@link normaliseRoute}. */
  route: string
  /** F2 path. Feed to `resolveSelectorPath`; may no longer resolve. */
  selectorPath: string
  /** Capture-time geometry. The fallback when the selector misses. */
  pinRect: PinRect
  /**
   * Viewport at capture time, or `null` when F1 did not record one. Drives
   * {@link scalePinRect}; without it the stored rect is used as-is.
   */
  capturedViewport: PinViewport | null
  /** Drives the marker tint. See `FEEDBACK_PIN_TINT`. */
  status: FeedbackStatus
  /** Comment count, rendered on the marker. */
  replyCount: number
  /**
   * Optional one-line preview for the popover header, rendered as TEXT and
   * never as HTML. Optional because F1 may elect not to ship message bodies
   * down to every page view.
   */
  summary?: string
}

/**
 * The F1 fetch, as a function type. The overlay accepts one of these so the
 * sibling's server action can be injected rather than imported.
 */
export type FeedbackPinAnchorLoader = (
  route: string,
) => Promise<FeedbackPinAnchor[]>

/**
 * Pins closer together than this collapse into one clustered marker.
 * 24px is roughly one marker width: any closer and the two markers overlap
 * enough that neither is independently clickable.
 */
export const PIN_CLUSTER_RADIUS_PX = 24

/**
 * Canonical form of a route for comparison: leading slash, no query string,
 * no hash, no trailing slash (except the root).
 *
 * Both sides are normalised before comparison because the stored route comes
 * from `window.location.pathname + search` at capture time while the live one
 * comes from `usePathname()`, and a pin must not vanish because the user
 * changed a tab query parameter.
 */
export function normaliseRoute(value: string): string {
  const withoutHash = value.split('#')[0]
  const withoutQuery = withoutHash.split('?')[0]
  const trimmed = withoutQuery.trim()
  if (trimmed === '' || trimmed === '/') return '/'
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return withSlash.endsWith('/') ? withSlash.slice(0, -1) : withSlash
}

/**
 * The anchors that belong on `route`.
 *
 * This is what makes edge case 4 ("route change → overlay clears before
 * refetch; no stale pins") a property of the data rather than of an effect's
 * timing: anchors fetched for the previous route stop matching the instant
 * the route changes, so the overlay renders nothing even if the refetch has
 * not resolved yet.
 */
export function anchorsForRoute(
  anchors: readonly FeedbackPinAnchor[],
  route: string,
): FeedbackPinAnchor[] {
  const current = normaliseRoute(route)
  return anchors.filter(a => normaliseRoute(a.route) === current)
}

/**
 * Rescale a capture-time rect into the current viewport.
 *
 * ONE ratio (the width ratio) is applied to both axes. A separate vertical
 * ratio taken from the viewport height would be wrong: page content reflows
 * horizontally and grows vertically, so a taller window does not push content
 * down proportionally — it just reveals more of it. The width ratio at least
 * tracks the layout the pin was measured against.
 *
 * Degenerate inputs (no capture viewport, or a zero/negative width) return
 * the stored rect untouched rather than a `NaN` rect, which would render as
 * an invisible marker and look like a missing pin rather than a bad one.
 */
export function scalePinRect(
  rect: PinRect,
  captured: PinViewport | null,
  current: PinViewport,
): PinRect {
  if (!captured || captured.width <= 0 || current.width <= 0) return rect
  const ratio = current.width / captured.width
  if (ratio === 1) return rect
  return {
    top: rect.top * ratio,
    left: rect.left * ratio,
    width: rect.width * ratio,
    height: rect.height * ratio,
  }
}
